// Preview a linked colour without changing the selected product.
// Delegation also covers cards inserted by filters, pagination and the theme editor.
(() => {
  const selector = 'a[data-swatch-preview-src], a[data-swatch-product-url]';
  const states = new WeakMap();
  const products = new Map();
  const attributes = ['src', 'srcset', 'sizes', 'alt'];

  function stateFor(link) {
    const card = link.closest('[data-card-swatch-preview]');
    const image = card?.querySelector('.fr-card-featured-image');
    if (!image) return null;
    if (!states.has(card)) {
      states.set(card, { card, image, original: attributes.map(name => [name, image.getAttribute(name)]), sequence: 0 });
    }
    return states.get(card);
  }

  function restore(state) {
    state.sequence++;
    state.active = null;
    state.card.classList.remove('fr-swatch-previewing');
    state.original.forEach(([name, value]) => value === null
      ? state.image.removeAttribute(name) : state.image.setAttribute(name, value));
  }

  function update(state) {
    const link = state.pointer || state.focus;
    if (!link) return restore(state);
    if (state.active === link) return;
    const sequence = ++state.sequence;
    state.active = link;
    if (link.dataset.swatchPreviewSrc) {
      return loadImage(state, link, sequence, link.dataset.swatchPreviewSrc,
        link.dataset.swatchPreviewSrcset || '', link.dataset.swatchPreviewAlt || '');
    }
    // Native listing children are fetched only on intent, never in the initial card render.
    const url = new URL(link.dataset.swatchProductUrl, location.origin);
    if (url.origin !== location.origin) return restore(state);
    const key = url.pathname;
    if (!products.has(key)) {
      products.set(key, fetch(`${key}.js`, { headers: { Accept: 'application/json' } })
        .then(response => { if (!response.ok) throw new Error('Product unavailable'); return response.json(); })
        .catch(error => { products.delete(key); throw error; }));
    }
    products.get(key).then(product => {
      if (sequence !== state.sequence || !state.card.isConnected) return;
      if (!product.featured_image) return restore(state);
      const imageUrl = new URL(product.featured_image, location.origin);
      const srcset = [165, 360, 600, 900].map(width => {
        imageUrl.searchParams.set('width', width);
        return `${imageUrl.href} ${width}w`;
      }).join(', ');
      imageUrl.searchParams.set('width', '600');
      loadImage(state, link, sequence, imageUrl.href, srcset, product.title || link.title);
    }).catch(() => { if (sequence === state.sequence) restore(state); });
  }

  function loadImage(state, link, sequence, src, srcset, alt) {
    const preload = new Image();
    preload.sizes = state.image.sizes || '300px';
    preload.srcset = srcset;
    preload.onload = () => {
      if (sequence !== state.sequence || !state.card.isConnected) return;
      state.image.srcset = preload.srcset;
      state.image.src = preload.src;
      state.image.alt = alt;
      state.card.classList.add('fr-swatch-previewing');
    };
    preload.onerror = () => {
      if (sequence === state.sequence) restore(state);
    };
    preload.src = src;
  }

  function enter(event, input) {
    if (input === 'pointer' && event.pointerType === 'touch') return;
    const link = event.target.closest?.(selector);
    if (!link || link.contains(event.relatedTarget)) return;
    const state = stateFor(link);
    if (!state) return;
    state[input] = link;
    update(state);
  }

  function leave(event, input) {
    const link = event.target.closest?.(selector);
    if (!link || link.contains(event.relatedTarget)) return;
    const state = stateFor(link);
    if (!state || state[input] !== link) return;
    const next = event.relatedTarget?.closest?.(selector);
    state[input] = next && state.card.contains(next) ? next : null;
    update(state);
  }

  document.addEventListener('pointerover', event => enter(event, 'pointer'));
  document.addEventListener('pointerout', event => leave(event, 'pointer'));
  document.addEventListener('focusin', event => enter(event, 'focus'));
  document.addEventListener('focusout', event => leave(event, 'focus'));
})();
